import PropTypes from 'prop-types';
import queryString from 'query-string';
import { addressProps, billingMethodProps } from '../declarations.propTypes';
import {
  ActionsIcon,
  AlloyButton,
  DownloadIcon,
  PrintIcon
} from 'cat-ecommerce-alloy';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import endpoints from '@app/constants/endpoints';
import {
  getOrderExportFileName,
  replaceTokensInString,
  replaceTokensInUrl
} from '@app/utils';
import MenuPopup from '@app/components/common/ExportMenuPopup';
import cx from 'classnames';
import { HEADER_ACTIONS_ICON_SIZE } from '../constants';
import Conditional from '@app/components/common/Conditional';
import { FILE_FORMATS } from '@app/constants/commonConstants';
import { PCC_PS_TITLE_UPDATES } from '@app/constants/featureFlags';

const SummaryAndPaymentHeaderActions = ({
  isPendingQuote,
  orderId,
  selectedBillingMethod,
  selectedBillingAddress,
  hideQuoteDetails
}) => {
  const { csv, xls } = FILE_FORMATS;
  const [t] = useTranslation();
  const langId = useSelector(s => s.common?.langId);
  const storeId = useSelector(s => s.common?.storeId);
  const catalogId = useSelector(s => s.common?.catalogId);
  const isQuotesAvailable = useSelector(
    s => s.orders?.byId?.[orderId]?.quoteAvailable
  );
  const titleUpdatesFlagEnabled = useSelector(
    state => state.featureFlag?.[PCC_PS_TITLE_UPDATES]
  );

  const timeZone = -new Date().getTimezoneOffset() / 60;
  const currentTimeZone = `GMT${timeZone > 0 ? '+' : '-'}${Math.abs(timeZone)}`;

  const getOrderSummaryExportUrl = format => {
    const fromPage = 'OrderSummary';
    const fileName = getOrderExportFileName(fromPage, orderId);
    const baseUrl = replaceTokensInUrl(
      endpoints.ORDER_EXPORT_LINK,
      storeId,
      langId,
      fileName,
      orderId,
      format,
      fromPage
    );

    const additionalParams = queryString.stringify({
      payMethod: selectedBillingMethod?.value ?? '',
      billToAddressId: selectedBillingAddress?.addressId ?? ''
    });

    return `${baseUrl}&${additionalParams}`;
  };

  const getQuoteDetailsExportUrl = format => {
    const fromPage = 'Quote';
    const fileName = getOrderExportFileName(fromPage, orderId);
    const baseUrl = replaceTokensInUrl(
      endpoints.ORDER_EXPORT_LINK,
      storeId,
      langId,
      fileName,
      orderId,
      format,
      fromPage
    );

    return `${baseUrl}`;
  };

  const getQuotePdf = () =>
    replaceTokensInUrl(
      endpoints.QUOTE_PDF_EXPORT_LINK,
      storeId,
      catalogId,
      orderId,
      currentTimeZone,
      langId
    );

  const downloadUrls = isPendingQuote
    ? [
        { url: getQuotePdf(), displayName: 'QUOTE_PDF' },
        { url: getQuoteDetailsExportUrl(xls), displayName: 'EXPORT_XLS' },
        { url: getQuoteDetailsExportUrl(csv), displayName: 'EXPORT_CSV' }
      ]
    : [
        { url: getOrderSummaryExportUrl(xls), displayName: 'EXPORT_XLS' },
        { url: getOrderSummaryExportUrl(csv), displayName: 'EXPORT_CSV' }
      ];

  const printLabel = t('PD_PRINT');
  const downloadLabel = replaceTokensInString(t('DOWNLOAD_{0}'), '');

  const isMenuPopupVisible = orderId && storeId;
  const hideButtons = isPendingQuote && !isQuotesAvailable;

  return (
    <Conditional test={!hideButtons && !hideQuoteDetails}>
      <Conditional test={isMenuPopupVisible}>
        <MenuPopup optionList={downloadUrls}>
          <AlloyButton
            aria-label={downloadLabel}
            buttonType="ghost"
            className="px-md-4 align-bottom"
          >
            <DownloadIcon
              title=""
              aria-label={`${downloadLabel}-icon`}
              width={HEADER_ACTIONS_ICON_SIZE}
              height={HEADER_ACTIONS_ICON_SIZE}
              className="d-none d-md-inline-block"
            />
            <ActionsIcon title="" aria-label="Actions" className="d-md-none" />
            <div
              className={cx('d-none d-md-block pt-1 text-xs', {
                'text-capitalize': titleUpdatesFlagEnabled
              })}
            >
              {downloadLabel}
            </div>
          </AlloyButton>
        </MenuPopup>
      </Conditional>

      <AlloyButton
        aria-label={printLabel}
        buttonType="transparent"
        className="px-4 d-none d-md-inline-block align-bottom"
        onClick={() => window.print()}
      >
        <PrintIcon
          title=""
          aria-label={`${printLabel}-icon`}
          width={HEADER_ACTIONS_ICON_SIZE}
          height={HEADER_ACTIONS_ICON_SIZE}
        />

        <div
          className={cx('pt-1 text-xs', {
            'text-capitalize': titleUpdatesFlagEnabled
          })}
        >
          {printLabel}
        </div>
      </AlloyButton>
    </Conditional>
  );
};

SummaryAndPaymentHeaderActions.propTypes = {
  isPendingQuote: PropTypes.bool,
  orderId: PropTypes.string,
  selectedBillingAddress: addressProps,
  selectedBillingMethod: billingMethodProps,
  hideQuoteDetails: PropTypes.bool
};

export default SummaryAndPaymentHeaderActions;
